import { OrganizationConfig, OrganizationRevenue } from "../models/revenue/BillingConfig";
import ApplicationLayoutModel from "../models/applications/api/ApplicationLayoutModel";
import ApplicationModel from "../models/applications/api/ApplicationModel";
import { compareRevenueApplications, getRevenueGroupName } from "./revenueNaming";

// Columns B-K and Act/Bill/$ of the "2026 Fixed-Variable Revenue" workbook tab, with Store ID and
// Corp/Fran combined into Group and QB omitted. Retail Health's inbound/outbound split and outbound rate
// follow, then Month for filtering and Application ID for matching rows, since app names often differ
// from the workbook's assistant names.
export const VARIABLE_REVENUE_HEADERS = [
    "Type", "Client Name", "Group", "Assistant Name",
    "Monthly Fixed Fee", "Variable Rate", "Included Calls", "Launch Date",
    "Act", "Bill", "$",
    "Inbound Minutes", "Outbound Minutes", "Inbound + Outbound Minutes", "Outbound Rate",
    "Month", "Application ID",
];

export const EXPORT_COLUMN = { group: 2, assistantName: 3, launchDate: 7, act: 8, bill: 9, amount: 10 };

// Workbook "Type" labels, in the order the tab lists them.
const WORKBOOK_TYPES: Record<string, string> = {
    "AI Answering": "Answering",
    "AI Ordering": "Ordering",
    "Medical": "Retail Health",
};

export type RevenueExportCell = string | number;

export interface LoadedRevenueData {
    month: string;
    organizationIds: string[];
}

// Data is current when the last load covered the selected month and every configured org.
// Billing settings changes are recalculated automatically, so they don't require a reload.
export const isDataLoadedFor = (
    loadedData: LoadedRevenueData | null,
    month: string,
    organizations: OrganizationConfig[]
): boolean =>
    !!loadedData && loadedData.month === month && organizations.length > 0
    && organizations.every(org => loadedData.organizationIds.includes(org.organizationId));

// Groups per-org load failures by reason so each distinct error is shown once with its org names.
export const summarizeOrgLoadErrors = (
    orgLoadErrors: Record<string, string>,
    organizations: OrganizationConfig[],
    names: Record<string, string>
): { reason: string; organizationNames: string[] }[] => {
    const byReason = new Map<string, string[]>();
    Object.keys(orgLoadErrors).forEach(orgId => {
        const org = organizations.find(item => item.organizationId === orgId);
        const name = names[orgId] || org?.organizationName || orgId;
        byReason.set(orgLoadErrors[orgId], [...(byReason.get(orgLoadErrors[orgId]) || []), name]);
    });
    return Array.from(byReason, ([reason, organizationNames]) => ({
        reason, organizationNames: organizationNames.sort((a, b) => a.localeCompare(b)),
    }));
};

export const isVariableExportContract = (contractType?: string): boolean =>
    contractType === "AI Answering" || contractType === "AI Ordering" || contractType === "Medical";

const requireNumber = (value: number, label: string): number => {
    if (!Number.isFinite(value) || value < 0) {
        throw new Error(`Invalid ${label}. Check the billing configuration and reload data.`);
    }
    return value;
};

const roundCents = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export const buildVariableRevenueExport = (
    month: string,
    organizations: OrganizationConfig[],
    revenues: OrganizationRevenue[],
    layouts: Record<string, ApplicationLayoutModel | null>,
    names: Record<string, string>,
    applicationsByOrg: Record<string, ApplicationModel[]> = {}
): RevenueExportCell[][] => {
    const rows: RevenueExportCell[][] = [];
    const seen = new Set<string>();
    const typeOrder = Object.keys(WORKBOOK_TYPES);
    const orgName = (org: OrganizationConfig) => names[org.organizationId] || org.organizationName || org.organizationId;
    organizations.filter(org => isVariableExportContract(org.contractType)).slice().sort((a, b) =>
        typeOrder.indexOf(a.contractType) - typeOrder.indexOf(b.contractType)
        || orgName(a).localeCompare(orgName(b))
        || a.organizationId.localeCompare(b.organizationId)
    ).forEach(org => {
        const revenue = revenues.find(item => item.organizationId === org.organizationId);
        if (!revenue) throw new Error(`Missing revenue data for ${org.organizationName || org.organizationId}.`);
        const layout = layouts[org.organizationId];
        revenue.applications.filter(app => !org.ignoredApplicationIds.includes(app.applicationId)).slice().sort((a, b) =>
            compareRevenueApplications(layout,
                { id: a.applicationId, name: a.applicationName },
                { id: b.applicationId, name: b.applicationName })
        ).forEach(app => {
            // Group labels may change; identity is based only on persistent IDs and service.
            const key = `${org.organizationId}/${app.applicationId}/${org.contractType}`;
            if (seen.has(key)) throw new Error(`Duplicate billing record: ${key}`);
            seen.add(key);
            const config = org.applicationOverrides.find(override => override.applicationId === app.applicationId)?.config || org.defaultConfig;
            const calls = requireNumber(app.breakdown.calls.count, "call count");
            const orders = requireNumber(app.breakdown.ordersPlaced.count, "order count");
            const inboundMinutes = requireNumber(app.breakdown.aiMinutes.count, "inbound minutes");
            const outboundMinutes = requireNumber(app.breakdown.outboundAiMinutes.count, "outbound minutes");
            const fixedFee = requireNumber(config.flatMonthlyFee, "monthly fixed fee");
            // Workbook formulas: Answering bills calls above the allowance; Ordering bills completed
            // orders; $ = Bill * rate. Retail Health bills inbound + outbound AI minutes, each at its
            // own rate (often the same), so $ = inbound * rate + outbound * outbound rate. $ is the
            // variable invoice amount only; the fixed fee is billed separately on the recurring invoice.
            let rate: number;
            let includedCalls: RevenueExportCell;
            let act: number;
            let bill: number;
            let variableAmount: number;
            let minuteBreakdown: RevenueExportCell[] = ["", "", "", ""];
            if (org.contractType === "AI Answering") {
                rate = requireNumber(config.callRate, "call rate");
                includedCalls = requireNumber(config.callsIncluded, "included calls");
                act = calls;
                bill = Math.max(0, calls - includedCalls);
                variableAmount = bill * rate;
            } else if (org.contractType === "AI Ordering") {
                rate = requireNumber(config.orderPlacedRate, "order rate");
                includedCalls = 0;
                act = bill = orders;
                variableAmount = bill * rate;
            } else {
                rate = requireNumber(config.aiMinuteRate, "AI minute rate");
                const outboundRate = requireNumber(config.outboundAiMinuteRate, "outbound AI minute rate");
                includedCalls = "N/A";
                act = bill = roundCents(inboundMinutes + outboundMinutes);
                variableAmount = inboundMinutes * rate + outboundMinutes * outboundRate;
                minuteBreakdown = [roundCents(inboundMinutes), roundCents(outboundMinutes), act, outboundRate];
            }
            const launchDate = applicationsByOrg[org.organizationId]?.find(item => item.id === app.applicationId)?.launchDate;
            // Missing or unparseable launch dates are left blank rather than guessed.
            const launchTimestamp = launchDate ? Date.parse(launchDate) : NaN;
            rows.push([
                // Group is the assistant group the app belongs to in Spark (the org's application layout).
                WORKBOOK_TYPES[org.contractType], orgName(org), getRevenueGroupName(layout, app.applicationId),
                app.applicationName || app.applicationId,
                fixedFee, rate, includedCalls,
                Number.isNaN(launchTimestamp) ? "" : new Date(launchTimestamp).toISOString().slice(0, 10),
                act, bill, roundCents(variableAmount),
                ...minuteBreakdown,
                // First of the month, so Excel reads it as a date for the workbook's month formulas.
                `${month}-01`, app.applicationId,
            ]);
        });
    });
    return rows;
};

// Copy for Excel omits headers so the rows paste directly beneath the workbook table's last row.
export const serializeVariableRevenueExport = (
    rows: RevenueExportCell[][],
    delimiter: "," | "\t",
    includeHeaders = true
): string =>
    (includeHeaders ? [VARIABLE_REVENUE_HEADERS, ...rows] : rows).map(row => row.map(value => {
        if (typeof value === "number") return String(requireNumber(value, "export value"));
        // Prevent spreadsheet formula execution from names, labels or imported IDs.
        const safe = /^[\s]*[=+\-@]/.test(value) ? "'" + value : value;
        const text = delimiter === "\t" ? safe.replace(/[\t\r\n]+/g, " ") : safe;
        return delimiter === "," && /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    }).join(delimiter)).join("\r\n");
